<template>
	<GlassCard class="weight-tracker p-3">
		<div class="d-flex align-items-center justify-content-between gap-2">
			<h2 class="mb-0">Weight</h2>
			<div class="d-flex align-items-center gap-2">
				<button
					v-if="
						Capacitor.isNativePlatform() &&
						Capacitor.getPlatform() === 'android' &&
						!userStore.isViewingUser()
					"
					@click="syncNow"
					:disabled="isSyncing"
					class="btn btn-sm btn-outline-success">
					<span
						v-if="isSyncing"
						class="spinner-border spinner-border-sm me-1"
						role="status"
						aria-hidden="true"></span>
					<i class="bi bi-arrow-repeat">&nbsp;</i
					><i class="bi bi-heart-pulse-fill"></i>
				</button>
				<button
					v-if="canEdit && isOnline"
					@click="openEditor()"
					class="btn btn-sm btn-primary">
					<i class="bi bi-plus-lg"></i> Add
				</button>
			</div>
		</div>

		<div class="btn-group w-100 mt-3" role="group" aria-label="View">
			<button
				type="button"
				class="btn btn-sm"
				:class="view === 'day' ? 'btn-light' : 'btn-outline-light'"
				@click="view = 'day'">
				Day
			</button>
			<button
				type="button"
				class="btn btn-sm"
				:class="view === 'trend' ? 'btn-light' : 'btn-outline-light'"
				@click="view = 'trend'">
				Trend
			</button>
		</div>

		<!-- Day navigation -->
		<div v-if="view === 'day'" class="day-nav mt-3">
			<button
				class="btn btn-outline-light btn-sm"
				@click="shiftDay(-1)"
				aria-label="Previous day">
				<i class="bi bi-chevron-left"></i>
			</button>
			<div class="day-label">
				<span>{{ dayLabel }}</span>
				<i class="bi bi-calendar3 ms-2"></i>
				<input
					type="date"
					class="day-input"
					:value="selectedDate"
					:max="today"
					aria-label="Pick a date"
					@click="$event.target.showPicker?.()"
					@change="onPickDate($event.target.value)" />
			</div>
			<button
				class="btn btn-outline-light btn-sm"
				:disabled="isToday"
				@click="shiftDay(1)"
				aria-label="Next day">
				<i class="bi bi-chevron-right"></i>
			</button>
			<button
				v-if="!isToday"
				class="btn btn-outline-light btn-sm"
				@click="selectedDate = today">
				Today
			</button>
		</div>

		<!-- Trend range -->
		<div v-else class="range-row mt-3" role="group" aria-label="Range">
			<button
				v-for="r in RANGES"
				:key="r.label"
				type="button"
				class="btn btn-sm"
				:class="trendDays === r.days ? 'btn-light' : 'btn-outline-light'"
				@click="trendDays = r.days">
				{{ r.label }}
			</button>
		</div>

		<div class="stat-grid mt-3">
			<div v-for="s in stats" :key="s.label" class="stat">
				<div class="stat-value">{{ s.value }}</div>
				<div class="stat-label">{{ s.label }}</div>
			</div>
		</div>

		<div
			class="mt-3 chart-container"
			@touchstart.passive="onTouchStart"
			@touchend.passive="onTouchEnd">
			<canvas id="weightChart" ref="weightCanvas"></canvas>
			<div v-if="loading" class="chart-overlay">Loading…</div>
			<div v-else-if="isEmpty" class="chart-overlay">
				{{
					view === "day"
						? "No weigh-ins for this day."
						: "No weigh-ins in this period."
				}}
			</div>
		</div>
		<div v-if="view === 'trend'" class="chart-caption">
			<template v-if="isWeekly">
				Line = weekly average, shaded band = weekly min–max.
			</template>
			<template v-else>
				Line = daily average, shaded band = daily min–max. Tap a day to open it.
			</template>
		</div>

		<!-- Day list -->
		<div v-if="view === 'day' && listRecords.length" class="reading-list mt-3">
			<div
				v-for="r in visibleRecords"
				:id="'w-row-' + r.id"
				:key="r.id"
				class="reading-row"
				:class="{
					highlight: highlightId === r.id,
					clickable: canEdit,
				}"
				@click="openEditor(r)">
				<span class="time">{{ fmtTime(r.timestamp) }}</span>
				<i
					v-if="r.from_health_connect"
					class="bi bi-heart-pulse-fill hc"
					title="Health Connect"></i>
				<span class="value ms-auto">
					{{ fmtWeight(r.weight) }} <small>lb</small>
				</span>
				<i v-if="canEdit" class="bi bi-pencil ms-2 edit-hint"></i>
			</div>
		</div>

		<!-- Trend list -->
		<div v-if="view === 'trend' && trendList.length" class="reading-list mt-3">
			<div
				v-for="d in visibleTrend"
				:key="d.date"
				class="reading-row"
				:class="{ clickable: !isWeekly }"
				@click="!isWeekly && goToDate(d.date)">
				<span class="time">{{ fmtBucket(d) }}</span>
				<span class="text-muted-light small ms-2">
					<template v-if="d.count > 1">
						{{ fmtWeight(d.min) }}–{{ fmtWeight(d.max) }} ·
					</template>
					{{ d.count }}×
				</span>
				<span class="value ms-auto">
					{{ fmtWeight(d.avg) }} <small>lb</small>
				</span>
				<i v-if="!isWeekly" class="bi bi-chevron-right ms-2 edit-hint"></i>
			</div>
		</div>

		<button
			v-if="hasMore"
			type="button"
			class="btn btn-sm btn-outline-light w-100 mt-2"
			@click="listLimit += PAGE">
			Show more ({{ totalRows - listLimit }} more)
		</button>
	</GlassCard>
</template>

<script setup>
	import {
		ref,
		computed,
		watch,
		onMounted,
		onBeforeUnmount,
		nextTick,
	} from "vue"
	import GlassCard from "./GlassCard.vue"
	import Chart from "chart.js/auto"
	import Swal from "sweetalert2"
	import moment from "moment-timezone"
	import { useTokenStore } from "@/stores/token"
	// import { useRouter } from "vue-router"
	// import { hasTokenIssue, handleTokenIssue } from "@/utils/tokenIssue"
	import { fetchWithAuth } from "@/utils/api-client"
	import { useUserStore } from "@/stores/user"
	import { isOnline } from "@/utils/networkBus"
	import * as SubscriptionHelper from "@/utils/subscriptionHelper"
	import { Capacitor } from "@capacitor/core"
	import { Health } from "@/plugins/Health"
	import { ensureTokenAndSyncStore } from "@/plugins/Health/sync"

	const userStore = useUserStore()
	const tokenStore = useTokenStore()
	const { hasToken } = tokenStore

	const DAY_FMT = "YYYY-MM-DD"
	const COLORS = {
		point: "orange",
		line: "white",
		band: "rgba(255, 165, 0, 0.2)",
	}

	const view = ref("day") // "day" | "trend"
	const today = ref(moment().format(DAY_FMT))
	const selectedDate = ref(today.value)
	// days: how far back the trend goes; 0 = all time
	const RANGES = [
		{ label: "1W", days: 7 },
		{ label: "1M", days: 30 },
		{ label: "3M", days: 90 },
		{ label: "6M", days: 182 },
		{ label: "1Y", days: 365 },
		{ label: "All", days: 0 },
	]
	const trendDays = ref(30)
	const dayCache = ref({}) // "YYYY-MM-DD" -> weigh-ins, oldest first
	const trendData = ref([]) // per-day (or per-week, for long "All") aggregates
	const isWeekly = computed(() => trendData.value[0]?.bucket === "week")
	const trendStale = ref(true)
	let trendLoadedDays = 0
	const loading = ref(false)
	const highlightId = ref(null)
	const weightCanvas = ref(null)
	let chart = null
	let chartRecords = [] // weigh-ins behind the current day chart, by index
	let highlightTimer = null

	// Health Connect sync (native app only)
	const isSyncing = ref(false)

	const canEdit = computed(() => hasToken() && !userStore.isViewingUser())
	const isToday = computed(() => selectedDate.value >= today.value)
	const dayLabel = computed(() =>
		moment(selectedDate.value, DAY_FMT).format("ddd, MMM D, YYYY")
	)
	const dayRecords = computed(() => dayCache.value[selectedDate.value] || [])
	const listRecords = computed(() => [...dayRecords.value].reverse())
	const trendList = computed(() => [...trendData.value].reverse())

	// only render a page of rows at a time; "Show more" reveals the rest
	const PAGE = 10
	const listLimit = ref(PAGE)
	const visibleRecords = computed(() => listRecords.value.slice(0, listLimit.value))
	const visibleTrend = computed(() => trendList.value.slice(0, listLimit.value))
	const totalRows = computed(() =>
		view.value === "day" ? listRecords.value.length : trendList.value.length
	)
	const hasMore = computed(() => totalRows.value > listLimit.value)
	const isEmpty = computed(() =>
		view.value === "day"
			? dayRecords.value.length === 0
			: trendData.value.length === 0
	)

	const fmtTime = (ts) => moment.unix(ts).format("h:mm A")
	function fmtBucket(d) {
		const m = moment(d.date, DAY_FMT)
		const year = m.year() === moment().year() ? "" : ", YYYY"
		return d.bucket === "week"
			? m.format(`[Week of] MMM D${year}`)
			: m.format(`ddd, MMM D${year}`)
	}
	const fmtWeight = (w) => (Math.round(Number(w) * 10) / 10).toFixed(1)

	const stats = computed(() => {
		const dash = "–"
		if (view.value === "day") {
			const rs = dayRecords.value
			if (!rs.length)
				return [
					{ label: "Latest", value: dash },
					{ label: "Average", value: dash },
					{ label: "Min – Max", value: dash },
					{ label: "Weigh-ins", value: dash },
				]
			const ws = rs.map((r) => r.weight)
			return [
				{ label: "Latest", value: fmtWeight(ws[ws.length - 1]) },
				{
					label: "Average",
					value: fmtWeight(ws.reduce((s, w) => s + w, 0) / ws.length),
				},
				{
					label: "Min – Max",
					value: `${fmtWeight(Math.min(...ws))}–${fmtWeight(Math.max(...ws))}`,
				},
				{ label: "Weigh-ins", value: rs.length },
			]
		}
		const ds = trendData.value
		if (!ds.length)
			return [
				{ label: "Latest", value: dash },
				{ label: "Change", value: dash },
				{ label: "Min – Max", value: dash },
				{ label: isWeekly.value ? "Weeks logged" : "Days logged", value: dash },
			]
		const change = ds[ds.length - 1].avg - ds[0].avg
		return [
			{ label: "Latest", value: fmtWeight(ds[ds.length - 1].avg) },
			{
				label: "Change",
				value: `${change > 0 ? "+" : ""}${fmtWeight(change)}`,
			},
			{
				label: "Min – Max",
				value: `${fmtWeight(Math.min(...ds.map((d) => d.min)))}–${fmtWeight(
					Math.max(...ds.map((d) => d.max))
				)}`,
			},
			{ label: isWeekly.value ? "Weeks logged" : "Days logged", value: ds.length },
		]
	})

	// ---- data loading -------------------------------------------------------

	async function apiGet(path) {
		const response = await fetchWithAuth(path, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				"User-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
			},
		})
		if (!response.ok) throw new Error("Network response was not ok")
		return response.json()
	}

	function dayBounds(date) {
		const m = moment(date, DAY_FMT)
		return [m.clone().startOf("day").unix(), m.clone().endOf("day").unix()]
	}

	async function loadDay(date) {
		if (dayCache.value[date]) return
		const [from, to] = dayBounds(date)
		dayCache.value[date] = await apiGet(
			`/api/weight-range?from=${from}&to=${to}`
		)
	}

	function prefetchNeighbors(date) {
		for (const offset of [-1, 1]) {
			const d = moment(date, DAY_FMT).add(offset, "day").format(DAY_FMT)
			if (d > today.value) continue
			loadDay(d).catch(() => {})
		}
	}

	async function loadTrend() {
		if (!trendStale.value && trendLoadedDays === trendDays.value) return
		const to = moment().endOf("day").unix()
		const from = moment()
			.startOf("day")
			.subtract(trendDays.value - 1, "days")
			.unix()
		const data = await apiGet(
			trendDays.value === 0
				? `/api/weight-range?all=1&to=${to}`
				: `/api/weight-range?from=${from}&to=${to}&bucket=day`
		)
		trendLoadedDays = trendDays.value
		trendData.value = data
		trendStale.value = false
	}

	let lastRefreshError = null
	async function refresh() {
		lastRefreshError = null
		const v = view.value
		const date = selectedDate.value
		const days = trendDays.value
		const isCurrent = () =>
			v === view.value &&
			(v === "day" ? date === selectedDate.value : days === trendDays.value)
		try {
			if (!dayCache.value[date] || v === "trend") loading.value = true
			if (v === "day") await loadDay(date)
			else await loadTrend()
		} catch (error) {
			lastRefreshError = error
			console.error("Failed to fetch weight records:", error)
		} finally {
			if (isCurrent()) loading.value = false
		}
		if (!isCurrent()) return
		await nextTick()
		renderChart()
		if (v === "day") prefetchNeighbors(date)
	}

	async function reloadAll() {
		dayCache.value = {}
		trendStale.value = true
		await refresh()
	}

	async function goToDate(date) {
		if (view.value === "day" && selectedDate.value === date) await refresh()
		else {
			view.value = "day"
			selectedDate.value = date
		}
	}

	watch([view, selectedDate, trendDays], () => {
		listLimit.value = PAGE
		refresh()
	})

	function shiftDay(delta) {
		const next = moment(selectedDate.value, DAY_FMT)
			.add(delta, "day")
			.format(DAY_FMT)
		if (next <= today.value) selectedDate.value = next
	}

	function onPickDate(value) {
		if (!value) return
		selectedDate.value = value > today.value ? today.value : value
	}

	// swipe left/right on the chart to change day
	let touchStart = null
	function onTouchStart(e) {
		const t = e.changedTouches[0]
		touchStart = { x: t.clientX, y: t.clientY }
	}
	function onTouchEnd(e) {
		if (view.value !== "day" || !touchStart) return
		const t = e.changedTouches[0]
		const dx = t.clientX - touchStart.x
		const dy = t.clientY - touchStart.y
		touchStart = null
		if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 2) {
			shiftDay(dx > 0 ? -1 : 1)
		}
	}

	// ---- chart --------------------------------------------------------------

	function baseOptions() {
		return {
			animation: false,
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: "nearest", intersect: true },
			plugins: { legend: { display: false } },
			scales: {
				y: {
					beginAtZero: false,
					ticks: { color: "white" },
					grid: { color: "rgba(255,255,255,0.1)" },
				},
			},
		}
	}

	function renderChart() {
		if (chart) {
			chart.destroy()
			chart = null
		}
		if (isEmpty.value) return
		const canvasEl = weightCanvas.value || document.getElementById("weightChart")
		const ctx =
			canvasEl && canvasEl.getContext ? canvasEl.getContext("2d") : null
		if (!ctx) {
			console.warn("Weight chart: canvas 2D context not available")
			return
		}
		chart = view.value === "day" ? buildDayChart(ctx) : buildTrendChart(ctx)
	}

	function buildDayChart(ctx) {
		const [dayStart] = dayBounds(selectedDate.value)
		chartRecords = dayRecords.value
		const opts = baseOptions()
		opts.scales.x = {
			type: "linear",
			min: 0,
			max: 1440,
			ticks: {
				color: "white",
				stepSize: 180,
				callback: (v) =>
					moment().startOf("day").add(v, "minutes").format("h A"),
			},
			grid: { color: "rgba(255,255,255,0.1)" },
		}
		opts.plugins.tooltip = {
			callbacks: {
				title: (items) => fmtTime(chartRecords[items[0].dataIndex].timestamp),
				label: (item) => `${fmtWeight(item.parsed.y)} lb`,
			},
		}
		opts.onClick = (_e, elements) => {
			if (!elements.length) return
			focusRow(chartRecords[elements[0].index].id)
		}
		return new Chart(ctx, {
			type: "line",
			data: {
				datasets: [
					{
						data: chartRecords.map((r) => ({
							x: (r.timestamp - dayStart) / 60,
							y: r.weight,
						})),
						borderColor: COLORS.line,
						backgroundColor: COLORS.point,
						borderWidth: 1.5,
						tension: 0.1,
						pointRadius: 5,
						pointHoverRadius: 7,
						pointHitRadius: 14,
					},
				],
			},
			options: opts,
		})
	}

	function buildTrendChart(ctx) {
		const weekly = isWeekly.value
		const end = moment().startOf("day")
		const first =
			trendDays.value === 0
				? moment(trendData.value[0].date, DAY_FMT)
				: end.clone().subtract(trendDays.value - 1, "days")
		const dates = []
		for (
			const cur = first.clone();
			!cur.isAfter(end);
			cur.add(weekly ? 7 : 1, "days")
		)
			dates.push(cur.format(DAY_FMT))
		const spanDays = end.diff(first, "days")
		const byDate = new Map(trendData.value.map((d) => [d.date, d]))
		const pick = (key) => dates.map((d) => byDate.get(d)?.[key] ?? null)
		const opts = baseOptions()
		opts.scales.x = {
			ticks: {
				color: "white",
				maxTicksLimit: 7,
				maxRotation: 0,
				callback(value) {
					return moment(dates[value], DAY_FMT).format(
						spanDays > 400 ? "MMM 'YY" : spanDays > 90 ? "MMM D" : "M/D"
					)
				},
			},
			grid: { color: "rgba(255,255,255,0.1)" },
		}
		opts.plugins.tooltip = {
			filter: (item) => item.datasetIndex === 2,
			callbacks: {
				title: (items) =>
					fmtBucket({
						date: dates[items[0].dataIndex],
						bucket: weekly ? "week" : "day",
					}),
				label: (item) => {
					const d = byDate.get(dates[item.dataIndex])
					if (!d) return ""
					const lines = [
						`${weekly ? "Weekly average" : "Average"} ${fmtWeight(d.avg)} lb`,
					]
					if (d.count > 1)
						lines.push(`Range ${fmtWeight(d.min)}–${fmtWeight(d.max)}`)
					lines.push(`${d.count} weigh-in${d.count === 1 ? "" : "s"}`)
					return lines
				},
			},
		}
		opts.onClick = (_e, elements) => {
			if (!elements.length) return
			const date = dates[elements[0].index]
			if (!weekly && byDate.has(date)) goToDate(date)
		}
		const n = trendData.value.length
		return new Chart(ctx, {
			type: "line",
			data: {
				labels: dates.map((_, i) => i),
				datasets: [
					{
						data: pick("max"),
						borderWidth: 0,
						pointRadius: 0,
						pointHitRadius: 0,
						fill: "+1",
						backgroundColor: COLORS.band,
					},
					{
						data: pick("min"),
						borderWidth: 0,
						pointRadius: 0,
						pointHitRadius: 0,
					},
					{
						data: pick("avg"),
						borderColor: COLORS.line,
						backgroundColor: COLORS.point,
						borderWidth: 2,
						tension: 0.1,
						spanGaps: true,
						pointRadius: n > 120 ? 0 : n > 60 ? 1.5 : n > 30 ? 2.5 : 4,
						pointHoverRadius: 6,
						pointHitRadius: 12,
					},
				],
			},
			options: opts,
		})
	}

	function focusRow(id) {
		const idx = listRecords.value.findIndex((r) => r.id === id)
		if (idx >= listLimit.value) listLimit.value = idx + 1
		highlightId.value = id
		clearTimeout(highlightTimer)
		highlightTimer = setTimeout(() => (highlightId.value = null), 2000)
		nextTick(() =>
			document
				.getElementById(`w-row-${id}`)
				?.scrollIntoView({ block: "nearest", behavior: "smooth" })
		)
	}

	// ---- add / edit / delete ------------------------------------------------

	function defaultNewMoment() {
		if (selectedDate.value === today.value) return moment()
		return moment(selectedDate.value, DAY_FMT).hour(12).minute(0)
	}

	async function openEditor(record = null) {
		if (!canEdit.value) return
		if (!record && !isOnline.value) return
		const when = record ? moment.unix(record.timestamp) : defaultNewMoment()
		const result = await Swal.fire({
			title: record ? "Edit weigh-in" : "Add weigh-in",
			html:
				`<label class="swal2-input-label" for="w-time">Date and time</label>` +
				`<input id="w-time" type="datetime-local" class="swal2-input" value="${when.format(
					"YYYY-MM-DDTHH:mm"
				)}">` +
				`<label class="swal2-input-label" for="w-value">Weight (lb)</label>` +
				`<input id="w-value" type="number" inputmode="decimal" step="0.1" min="0" class="swal2-input" value="${
					record ? fmtWeight(record.weight) : ""
				}">`,
			showCancelButton: true,
			showDenyButton: !!record,
			confirmButtonText: "Save",
			denyButtonText: "Delete",
			focusConfirm: false,
			didOpen: () => document.getElementById("w-value")?.focus(),
			preConfirm: () => {
				const dateTime = document.getElementById("w-time").value
				const weight =
					Math.round(Number(document.getElementById("w-value").value) * 10) / 10
				if (!dateTime || !(weight > 0)) {
					Swal.showValidationMessage(
						"Please fill out the date/time and weight fields."
					)
					return false
				}
				return { ts: moment(dateTime).unix(), weight }
			},
		})
		if (result.isDenied && record) {
			const confirm = await Swal.fire({
				title: "Are you sure you want to delete this record?",
				showCancelButton: true,
				confirmButtonText: "Delete",
			})
			if (confirm.isConfirmed) await deleteRecord(record)
		} else if (result.isConfirmed) {
			const { ts, weight } = result.value
			if (record) await updateRecord(record, ts, weight)
			else await addRecord(ts, weight)
		}
	}

	function cacheRemove(record) {
		for (const date of Object.keys(dayCache.value)) {
			const list = dayCache.value[date]
			const i = list.findIndex((r) => r.id === record.id)
			if (i > -1) list.splice(i, 1)
		}
	}

	function cacheInsert(record) {
		const date = moment.unix(record.timestamp).format(DAY_FMT)
		const list = dayCache.value[date]
		if (!list) return
		list.push(record)
		list.sort((a, b) => a.timestamp - b.timestamp)
	}

	// Health Connect helpers (Android native app only)
	async function canWriteHealthConnect() {
		if (
			!Capacitor.isNativePlatform() ||
			Capacitor.getPlatform() !== "android"
		)
			return false
		const { available } = await Health.isAvailable()
		if (!available) return false
		const gp = await Health.getGrantedPermissions().catch(() => ({
			granted: [],
		}))
		return Array.isArray(gp?.granted)
			? gp.granted.some((s) => String(s).includes("WRITE_WEIGHT"))
			: false
	}

	async function writeHealthConnect(iso, weightLb) {
		const wr = await Health.writeWeight({
			value: Number(weightLb),
			unit: "lb",
			time: iso,
		})
		if (wr && wr.id) {
			await fetchWithAuth("/api/health-connect/weights/upsert", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					identifier: wr.id,
					time: iso,
					weight: Number(weightLb),
					source: "android.healthconnect",
				}),
			})
		}
	}

	async function deleteHealthConnect(id, iso) {
		const mapRes = await fetchWithAuth(
			`/api/health-connect/weights/map/${id}`,
			{ method: "GET" }
		)
		if (!mapRes.ok) return
		const map = await mapRes.json()
		await Health.deleteWeight({ id: map?.identifier, time: iso })
	}

	async function addRecord(ts, weight) {
		const feature = "Add Weight"
		if (SubscriptionHelper.isFreeAccount()) {
			SubscriptionHelper.showLockedModal(feature)
			return
		}
		try {
			const date = moment.unix(ts).format(DAY_FMT)
			await loadDay(date)
			if (dayCache.value[date].some((r) => r.timestamp === ts)) {
				Swal.fire({
					icon: "error",
					title: "Oops...",
					text: "A record for this date/time already exists.",
				})
				return
			}
			const response = await fetchWithAuth("/api/weights", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ timestamp: ts, weight }),
			})
			if (
				!(await SubscriptionHelper.requestCheckSubscription(response, feature))
			)
				return
			if (!response.ok) throw new Error("Could not add record")

			const data = await response.json()
			cacheInsert({ id: data.id, timestamp: ts, weight, from_health_connect: false })
			trendStale.value = true

			// If Android native, write to Health Connect (lb) and map the identifier in backend
			try {
				if (await canWriteHealthConnect()) {
					await writeHealthConnect(moment.unix(ts).toISOString(), weight)
				}
			} catch (e) {
				console.warn("HC write/map for weight failed", e)
			}
			await goToDate(date)
		} catch (error) {
			alert(error.message)
			console.error("Failed to add or update the record:", error)
		}
	}

	async function deleteRecord(record) {
		try {
			// Android HC delete first using mapping & timestamp
			try {
				if (await canWriteHealthConnect()) {
					try {
						await deleteHealthConnect(
							record.id,
							moment.unix(record.timestamp).toISOString()
						)
					} catch (e) {
						console.warn("HC weight delete failed", e)
					}
				}
			} catch (e) {
				console.warn("HC weight map failed", e)
			}

			const response = await fetchWithAuth(`/api/weights/${record.id}`, {
				method: "DELETE",
				headers: { "Content-Type": "application/json" },
			})
			if (!response.ok) throw new Error("Network response was not ok")
			cacheRemove(record)
			trendStale.value = true
			await refresh()
		} catch (error) {
			console.error("Failed to delete the record:", error)
		}
	}

	async function updateRecord(record, ts, weight) {
		try {
			const timeChanged = ts !== record.timestamp
			const newDate = moment.unix(ts).format(DAY_FMT)
			if (timeChanged) await loadDay(newDate)
			const response = await fetchWithAuth(`/api/weights/${record.id}`, {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(timeChanged ? { weight, timestamp: ts } : { weight }),
			})
			if (response.status === 409) {
				Swal.fire({
					icon: "error",
					title: "Oops...",
					text: "A record for this date/time already exists.",
				})
				return
			}
			if (!response.ok) throw new Error("Network response was not ok")

			const oldIso = moment.unix(record.timestamp).toISOString()
			cacheRemove(record)
			cacheInsert({ ...record, timestamp: ts, weight })
			trendStale.value = true

			// Android: propagate update to HC by delete-by-time (and id if mapped), then write
			try {
				if (await canWriteHealthConnect()) {
					try {
						await deleteHealthConnect(record.id, oldIso)
					} catch (e) {
						console.warn("HC weight delete before update failed", e)
					}
					try {
						await writeHealthConnect(moment.unix(ts).toISOString(), weight)
					} catch (e) {
						console.warn("HC weight write after update failed", e)
					}
				}
			} catch (e) {
				console.warn("Failed to propagate weight update to HC", e)
			}
			await goToDate(newDate)
		} catch (error) {
			console.error("Failed to update the record:", error)
		}
	}

	onMounted(() => refresh())

	onBeforeUnmount(() => {
		clearTimeout(highlightTimer)
		if (chart) chart.destroy()
	})

	async function upsertHealthConnectWeightRecord(record) {
		const weightLb = Number(record?.valueLb)
		if (!record?.id || !record?.time || !Number.isFinite(weightLb)) {
			throw new Error(
				`Invalid Health Connect weight record: ${JSON.stringify(record)}`
			)
		}

		const response = await fetchWithAuth("/api/health-connect/weights/upsert", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				identifier: record.id,
				time: record.time,
				weight: +weightLb.toFixed(2),
				source: "android.healthconnect",
			}),
		})
		if (!response.ok) {
			throw new Error(
				`Health Connect weight upsert failed (${response.status}): ${await response.text()}`
			)
		}
	}

	// Show which Health Connect sync step failed so the error is actionable.
	let syncStage = "start"
	function syncErrorHtml(stage, e) {
		const esc = (s) =>
			String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]))
		return (
			`<div style="text-align:left;font-size:.85rem;word-break:break-word">` +
			`<b>Step:</b> ${esc(stage)}<br>` +
			`<b>Error:</b> ${esc(e?.name || "Error")}: ${esc(e?.message || e)}` +
			`</div>`
		)
	}

	async function syncNow() {
		// Do not allow syncing when viewing another member's data
		if (userStore.isViewingUser()) return
		const feature = "Add Weight"
		if (SubscriptionHelper.isFreeAccount()) {
			SubscriptionHelper.showLockedModal(feature)
			return
		}

		// Guard: Android native only
		if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android")
			return
		if (isSyncing.value) return
		let hasReadW = false
		let hasWriteW = false
		try {
			isSyncing.value = true
			syncStage = "check Health Connect availability and permissions"
			const { available } = await Health.isAvailable()
			if (!available) return
			const gp = await Health.getGrantedPermissions()
			hasWriteW = Array.isArray(gp?.granted)
				? gp.granted.some((s) => String(s).includes("WRITE_WEIGHT"))
				: false
			hasReadW = Array.isArray(gp?.granted)
				? gp.granted.some((s) => String(s).includes("READ_WEIGHT"))
				: false

			if (!hasReadW) {
				Swal.fire({
					icon: "warning",
					title: "Health Connect read permission is required to sync.",
					showConfirmButton: true,
				})
				return
			}
			Swal.fire({
				toast: true,
				position: "top",
				icon: "info",
				title: "Syncing…",
				showConfirmButton: false,
				timer: 1000,
			})
			syncStage = "pull changes from Health Connect"
			await applyHealthConnectChangesForWeight()
			syncStage = "reload data from server"
			await reloadAll()
			if (lastRefreshError) throw lastRefreshError
			// Only attempt to reconcile (write/delete to Health Connect) if WRITE is granted
			syncStage = "write app entries to Health Connect"
			await reconcileHealthConnectWithBackend(hasWriteW)
			const statusMsg = hasWriteW
				? `Synced Health Connect`
				: `Synced reads from Health Connect`
			Swal.fire({
				toast: true,
				position: "top",
				icon: "success",
				title: statusMsg,
				showConfirmButton: false,
				timer: 1500,
			})
		} catch (e) {
			console.warn("Weight sync failed", e)
			Swal.fire({
				icon: "error",
				title: "Sync failed",
				html: syncErrorHtml(syncStage, e),
			})
		} finally {
			isSyncing.value = false
		}
	}

	async function applyHealthConnectChangesForWeight() {
		const tokenKey = "weight"
		const recordTypes = ["WEIGHT"]

		await ensureTokenAndSyncStore(tokenKey, recordTypes)

		const deleted = []
		const upserts = []
		let total = 0
		let lastNextToken
		while (true) {
			const res = await Health.getChanges({
				tokenKey,
				recordTypes,
				token: lastNextToken,
			})
			console.log("weight changes", JSON.stringify(res))
			if (res.changes) {
				for (const ch of res.changes) {
					if (ch.changeType === "delete" && ch.recordType === "WEIGHT") {
						deleted.push(ch)
					} else if (ch.changeType === "upsert" && ch.recordType === "WEIGHT") {
						upserts.push(ch)
					}
				}
				total += res.changes.length
			}
			lastNextToken = res.nextToken || lastNextToken
			if (!res.hasMore) break
		}

		const errors = []
		// Apply deletions first so a deleted record cannot be recreated by a stale mapping.
		for (const ch of deleted) {
			try {
				const res = await fetchWithAuth("/api/health-connect/weights/delete", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						identifier: ch.recordId,
						source: "android.healthconnect",
					}),
				})
				if (!res.ok) {
					errors.push(`delete ${ch.recordId}: ${await res.text()}`)
				}
			} catch (e) {
				errors.push(`delete ${ch.recordId}: ${e?.message || e}`)
			}
		}

		// Apply changes from Health Connect, including records written by other apps.
		for (const ch of upserts) {
			try {
				await upsertHealthConnectWeightRecord(ch.record || {})
			} catch (e) {
				errors.push(`upsert: ${e?.message || e}`)
			}
		}

		if (errors.length) {
			throw new Error(
				`Health Connect weight sync failed for ${errors.length} record${
					errors.length === 1 ? "" : "s"
				}: ${errors.slice(0, 3).join(" | ")}`
			)
		}

		if (lastNextToken) {
			await Health.saveChangesToken({
				tokenKey,
				token: String(lastNextToken),
			})
		}
		return { total }
	}

	async function reconcileHealthConnectWithBackend(hasWriteW) {
		try {
			if (!hasWriteW) {
				console.warn("Skipping HC weight reconcile: WRITE_WEIGHT not granted")
				return
			}

			const cutoff = moment().subtract(30, "days").unix()
			const startIso = moment.unix(cutoff).toISOString()
			const endIso = moment().toISOString()
			const hc = await Health.readWeight({
				startTime: startIso,
				endTime: endIso,
				unit: "lb",
			})
			const hcMap = new Map()
			for (const r of hc.records || []) {
				const ts = moment(r.time).unix()
				// const lb =
				// 	typeof r.valueKg === "number" ? r.valueKg * 2.2046226218 : undefined
				const lb = typeof r.valueLb === "number" ? r.valueLb : undefined
				if (typeof lb === "number") hcMap.set(ts, { id: r.id, valueLb: lb })
			}
			const recent = (
				await apiGet(`/api/weight-range?from=${cutoff}&to=${moment().unix()}`)
			).filter((r) => typeof r.timestamp === "number")
			for (const rec of recent) {
				const ts = rec.timestamp
				const lb = Number(rec.weight)
				const iso = moment.unix(ts).toISOString()
				const hcRec = hcMap.get(ts)
				console.log(
					"Reconciling weight",
					JSON.stringify({
						ts,
						lb,
						hc: hcRec,
					})
				)
				// If missing in HC, or value differs beyond tolerance, write/update HC
				const tolerance = 0.1
				if (!hcRec) {
					const wr = await Health.writeWeight({
						value: lb,
						unit: "lb",
						time: iso,
					})
					if (!wr?.id)
						throw new Error("Health Connect did not return an ID for the weight write")
					const response = await fetchWithAuth("/api/health-connect/weights/upsert", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							identifier: wr.id,
						time: iso,
							weight: +lb.toFixed(2),
							source: "android.healthconnect",
						}),
					})
					if (!response.ok)
						throw new Error(
							`Health Connect weight mapping failed (${response.status}): ${await response.text()}`
						)
				} else if (
					typeof hcRec.valueLb === "number" &&
					typeof lb === "number" &&
					Math.abs(hcRec.valueLb - lb) > tolerance
				) {
					// Value differs — delete the existing HC record by id/time, then write the app value
					await Health.deleteWeight({ id: hcRec.id, time: iso })
					const wr = await Health.writeWeight({
						value: lb,
						unit: "lb",
						time: iso,
					})
					if (!wr?.id)
						throw new Error("Health Connect did not return an ID for the weight write")
					const response = await fetchWithAuth("/api/health-connect/weights/upsert", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							identifier: wr.id,
							time: iso,
							weight: +lb.toFixed(2),
							source: "android.healthconnect",
						}),
					})
					if (!response.ok)
						throw new Error(
							`Health Connect weight mapping failed (${response.status}): ${await response.text()}`
						)
				}
			}
		} catch (e) {
			console.warn("Reconcile HC weight with backend failed", e)
			throw e
		}
	}
</script>

<style lang="less" scoped>
	.weight-tracker {
		max-width: 600px;
		margin: auto;

		.day-nav {
			display: flex;
			align-items: center;
			gap: 0.5rem;
			.day-label {
				position: relative;
				flex: 1;
				text-align: center;
				color: white;
				font-weight: 600;
				padding: 0.25rem 0;
				.day-input {
					position: absolute;
					inset: 0;
					width: 100%;
					height: 100%;
					opacity: 0;
					cursor: pointer;
				}
			}
		}

		.range-row {
			display: flex;
			gap: 0.4rem;
			overflow-x: auto;
			.btn {
				flex: 1 0 auto;
			}
		}

		.stat-grid {
			display: grid;
			grid-template-columns: repeat(4, 1fr);
			gap: 0.5rem;
			.stat {
				background: rgba(255, 255, 255, 0.06);
				border-radius: 0.75rem;
				padding: 0.4rem 0.25rem;
				text-align: center;
				.stat-value {
					color: white;
					font-weight: 700;
					font-size: 1.05rem;
					&.low {
						color: #ef4444;
					}
					&.in {
						color: #2ecc71;
					}
					&.high {
						color: #fbbf24;
					}
				}
				.stat-label {
					color: rgba(255, 255, 255, 0.65);
					font-size: 0.7rem;
				}
			}
		}

		.chart-container {
			position: relative;
			width: 100%;
			height: 240px;
			canvas {
				display: block;
				width: 100% !important;
				height: 100% !important;
			}
			.chart-overlay {
				position: absolute;
				inset: 0;
				display: flex;
				align-items: center;
				justify-content: center;
				color: rgba(255, 255, 255, 0.75);
				pointer-events: none;
			}
		}
		.chart-caption {
			color: rgba(255, 255, 255, 0.6);
			font-size: 0.75rem;
			text-align: center;
			margin-top: 0.25rem;
		}

		.reading-list {
			.reading-row {
				display: flex;
				align-items: center;
				gap: 0.6rem;
				padding: 0.7rem 0.5rem;
				min-height: 48px;
				color: white;
				border-bottom: 1px solid rgba(255, 255, 255, 0.1);
				border-radius: 0.5rem;
				&.clickable {
					cursor: pointer;
					&:hover {
						background: rgba(255, 255, 255, 0.08);
					}
				}
				&.highlight {
					background: rgba(75, 192, 192, 0.25);
				}
				.dot {
					width: 0.75rem;
					height: 0.75rem;
					border-radius: 50%;
					flex: none;
					&.low {
						background: #ef4444;
					}
					&.in {
						background: #2ecc71;
					}
					&.high {
						background: #fbbf24;
					}
				}
				.hc {
					color: #2ecc71;
					font-size: 0.8rem;
				}
				.value {
					font-weight: 700;
					small {
						font-weight: 400;
						opacity: 0.65;
					}
				}
				.edit-hint {
					opacity: 0.5;
				}
				.text-muted-light {
					color: rgba(255, 255, 255, 0.6);
				}
			}
		}
	}
</style>
